/* StyleProfileSelector — One-click apply of a saved Style Profile to an AI
 * generation form. Renders a dropdown of the caller's saved profiles
 * (filtered by current client_id when supplied) plus quick actions:
 *   • Save current as profile…   (opens inline create modal)
 *   • Manage profiles            (deep-link to /style-profiles)
 *
 * Applying a profile populates: styleExample, extraInstructions, captionLength.
 *
 * Props:
 *   clientId?: string                       // when set, only profiles for this client + globals are listed
 *   currentStyleExample: string             // what the form currently has — for "Save as"
 *   currentExtraInstructions: string
 *   currentCaptionLength: { mode, words? }
 *   onApply({ styleExample, extraInstructions, captionLength }): void
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Save, Settings as SettingsIcon, Loader2 } from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const NONE_VALUE = '__none__';
const SAVE_VALUE = '__save_current__';
const MANAGE_VALUE = '__manage__';

export const StyleProfileSelector = ({
  clientId = null,
  currentStyleExample = '',
  currentExtraInstructions = '',
  currentCaptionLength = { mode: 'auto' },
  onApply,
}) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(NONE_VALUE);
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveBindToClient, setSaveBindToClient] = useState(Boolean(clientId));
  const [saving, setSaving] = useState(false);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = clientId ? `?client_id=${encodeURIComponent(clientId)}` : '';
      const r = await api.get(`/style-profiles${params}`);
      setProfiles(r.data?.profiles || []);
    } catch (_) {
      // Silent — not critical
    } finally {
      setLoading(false);
    }
  }, [api, clientId]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const handleSelect = (val) => {
    if (val === SAVE_VALUE) {
      // Don't change the active value yet — open the modal first.
      setSaveName('');
      setSaveBindToClient(Boolean(clientId));
      setShowSave(true);
      return;
    }
    if (val === MANAGE_VALUE) {
      navigate('/style-profiles');
      return;
    }
    if (val === NONE_VALUE) {
      setActiveId(NONE_VALUE);
      return;
    }
    const p = profiles.find((x) => x.id === val);
    if (!p) return;
    setActiveId(p.id);
    onApply?.({
      styleExample: p.style_example || '',
      extraInstructions: p.extra_instructions || '',
      captionLength:
        p.caption_length_mode === 'fixed' && p.caption_length_words
          ? { mode: 'fixed', words: p.caption_length_words }
          : p.caption_length_mode === 'per_platform'
            ? { mode: 'per_platform' }
            : { mode: 'auto' },
    });
    toast.success(`Style profile applied: ${p.name}`);
  };

  const handleSave = async () => {
    if (!saveName.trim()) {
      toast.error('Give the profile a name');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post('/style-profiles', {
        name: saveName.trim(),
        client_id: saveBindToClient && clientId ? clientId : null,
        style_example: currentStyleExample?.trim() || null,
        extra_instructions: currentExtraInstructions?.trim() || null,
        caption_length_mode: currentCaptionLength?.mode || 'auto',
        caption_length_words:
          currentCaptionLength?.mode === 'fixed' ? currentCaptionLength?.words : null,
      });
      toast.success(`Saved "${r.data.name}"`);
      setShowSave(false);
      setProfiles((p) => [...p, r.data].sort((a, b) => a.name.localeCompare(b.name)));
      setActiveId(r.data.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1.5" data-testid="style-profile-selector">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-zinc-700 flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-indigo-600" />
          Style profile
        </Label>
        {loading && <Loader2 className="w-3 h-3 animate-spin text-zinc-400" />}
      </div>
      <Select value={activeId} onValueChange={handleSelect}>
        <SelectTrigger data-testid="style-profile-trigger">
          <SelectValue placeholder="— No style profile —" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE} data-testid="style-profile-none">
            — No style profile —
          </SelectItem>
          {profiles.length > 0 && (
            <>
              <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-zinc-400">Your profiles</div>
              {profiles.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`style-profile-${p.id}`}>
                  <span className="flex items-center gap-2">
                    <span>{p.name}</span>
                    {p.client_id ? (
                      <span className="text-[10px] text-zinc-400">· client</span>
                    ) : (
                      <span className="text-[10px] text-zinc-400">· global</span>
                    )}
                  </span>
                </SelectItem>
              ))}
            </>
          )}
          <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-zinc-400 border-t mt-1">Actions</div>
          <SelectItem value={SAVE_VALUE} data-testid="style-profile-save-current">
            <span className="flex items-center gap-2 text-indigo-700">
              <Save className="w-3 h-3" /> Save current as profile…
            </span>
          </SelectItem>
          <SelectItem value={MANAGE_VALUE} data-testid="style-profile-manage">
            <span className="flex items-center gap-2 text-zinc-700">
              <SettingsIcon className="w-3 h-3" /> Manage profiles
            </span>
          </SelectItem>
        </SelectContent>
      </Select>
      <p className="text-[11px] text-zinc-500 leading-snug">
        One-click voice templates. Saved profiles fill the "Add more detail" panel + caption length below.
      </p>

      {/* Save-as inline modal */}
      <Dialog open={showSave} onOpenChange={setShowSave}>
        <DialogContent className="sm:max-w-md" data-testid="save-style-profile-dialog">
          <DialogHeader>
            <DialogTitle>Save style profile</DialogTitle>
            <DialogDescription>
              Captures the current style example, extra instructions, and caption length for one-click reuse.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="sp-name">Profile name</Label>
              <Input
                id="sp-name"
                data-testid="save-style-profile-name"
                placeholder="e.g. Founder voice, Educational thread style"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                maxLength={120}
              />
            </div>
            {clientId && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={saveBindToClient}
                  onChange={(e) => setSaveBindToClient(e.target.checked)}
                  data-testid="save-style-profile-bind-client"
                />
                <span>Tie this profile to the current client (otherwise it's global)</span>
              </label>
            )}
            <div className="text-[11px] text-zinc-500 rounded border border-zinc-200 bg-zinc-50 p-2 space-y-1">
              <p><strong>Will save:</strong></p>
              <p>• Style example: {currentStyleExample ? `${currentStyleExample.length} chars` : 'empty'}</p>
              <p>• Extra instructions: {currentExtraInstructions ? `${currentExtraInstructions.length} chars` : 'empty'}</p>
              <p>• Caption length: {
                currentCaptionLength?.mode === 'fixed'
                  ? `Fixed ~${currentCaptionLength.words}w`
                  : currentCaptionLength?.mode === 'per_platform'
                    ? 'Per-platform smart defaults'
                    : 'Auto'
              }</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSave(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-style-profile-submit">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Save profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StyleProfileSelector;
